import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Timeline from "antd/es/timeline";
import cn from "classnames";
import { differenceInHours } from "date-fns/esm";
import { defineMessages, useIntl } from "react-intl";

import LocationType from "@mapmycustomers/shared/enum/LocationType";
import { Activity, EntityType } from "@mapmycustomers/shared/types/entity";
import Location from "@mapmycustomers/shared/types/Location";
import { getPreviewEntityUniqueElementId } from "@mapmycustomers/shared/util/browser";

import noLocations from "@app/assets/map/legends/userLocations/noLocations.svg";
import ButtonLink from "@app/component/ButtonLink";
import { highlightLocationGroupId } from "@app/scene/map/store/actions";
import {
  getHighlightedLocationGroup,
  getLastLocationWithoutPeriod,
} from "@app/scene/map/store/selectors";
import { showEntityView } from "@app/store/entityView/actions";
import { RootState } from "@app/store/rootReducer";
import LocationGroup from "@app/types/map/LocationGroup";
import { formatFriendlyRelativeTime } from "@app/util/formatters";
import { parseApiDate } from "@app/util/parsers";

import styles from "./LocationRecords.module.scss";

const typeMessages = defineMessages({
  checkInButDeleted: {
    id: "map.layer.userLocation.location.type.checkInButDeleted",
    defaultMessage: "Check-in with {location} but was later deleted",
    description: "Map - user location pane - location item - type: check in but was later deleted",
  },
  [LocationType.APP_START]: {
    id: "map.layer.userLocation.location.type.appStart",
    defaultMessage: "App startup",
    description: "Map - user location pane - location item - type: app start",
  },
  [LocationType.AUTO_VISIT]: {
    id: "map.layer.userLocation.location.type.autoVisit",
    defaultMessage: "Location Ping via Auto Visits",
    description: "Map - user location pane - location item - type: Auto visit",
  },
  [LocationType.CHECK_IN]: {
    id: "map.layer.userLocation.location.type.checkIn",
    defaultMessage: "Check-in in with {location}",
    description: "Map - user location pane - location item - type: check in",
  },
  [LocationType.FIND_ME]: {
    id: "map.layer.userLocation.location.type.findMe",
    defaultMessage: "Used “Locate Me” Button",
    description: "Map - user location pane - location item - type: Find me",
  },
});

interface Props {
  clickedLocationGroupId?: LocationGroup["id"];
  highlightedLocationId?: LocationGroup["id"];
  highlightLocationGroupId: typeof highlightLocationGroupId;
  lastLocation?: Location;
  locationGroups: LocationGroup[];
  onClick: (locationGroup: LocationGroup) => void;
  showEntityView: typeof showEntityView;
}

const LocationRecords: React.FC<Props> = ({
  clickedLocationGroupId,
  highlightedLocationId,
  highlightLocationGroupId,
  lastLocation,
  locationGroups,
  onClick,
  showEntityView,
}) => {
  const intl = useIntl();
  useEffect(() => {
    if (highlightedLocationId) {
      const elements = document.getElementsByClassName(
        `user-location-item-${highlightedLocationId}`
      );
      if (elements.length) {
        elements[0].scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [highlightedLocationId]);

  const [last24hoursIndex, earlierIndex] = useMemo(() => {
    const result: (number | undefined)[] = [undefined, undefined];
    locationGroups
      .reduce<Location[]>((result, group) => [...result, ...group.locations], [])
      .forEach((location, index) => {
        const createdAt = parseApiDate(location.createdAt);
        if (differenceInHours(new Date(), createdAt) <= 24 && result[0] === undefined) {
          result[0] = index;
        }
        if (differenceInHours(new Date(), createdAt) > 24 && result[1] === undefined) {
          result[1] = index;
        }
      });
    return result;
  }, [locationGroups]);

  const getSubTitle = useCallback(
    (type: LocationType | null, crmActivity: Activity | null) => {
      return type
        ? crmActivity?.deletedAt
          ? intl.formatMessage(typeMessages.checkInButDeleted, {
              location: crmActivity?.name ?? "",
            })
          : intl.formatMessage(typeMessages[type], { location: crmActivity?.name ?? "" })
        : "";
    },
    [intl]
  );

  const handleActivityClick = useCallback(
    (event, crmActivity) => {
      showEntityView({
        elementId: getPreviewEntityUniqueElementId(event.target as HTMLElement),
        entityId: crmActivity.id,
        entityType: EntityType.ACTIVITY,
      });
    },
    [showEntityView]
  );

  return (
    <div className={styles.container}>
      {locationGroups.length === 0 && (
        <div className={styles.noLocations}>
          <img alt="no-locations" src={noLocations} />
          <div className={styles.noLocationsTitle}>
            {intl.formatMessage({
              id: "map.layer.userLocation.locations.noLocation",
              defaultMessage: "No location data within selected time range",
              description: "Map - user location pane - location list - no location title",
            })}
            {lastLocation && <div>{getSubTitle(lastLocation.type, lastLocation.crmActivity)}</div>}
          </div>
        </div>
      )}
      <Timeline>
        {locationGroups.map((group) =>
          group.locations.map((location, index) => (
            <Timeline.Item
              dot={
                location.type === LocationType.CHECK_IN ? (
                  <FontAwesomeIcon icon={faCheckCircle} />
                ) : (
                  <div className={styles.point} />
                )
              }
              key={location.id}
            >
              <div
                className={cn(
                  `user-location-item-${location.id}`,
                  styles.item,
                  {
                    [styles.selected]: highlightedLocationId === group.id,
                  },
                  { [styles.clicked]: clickedLocationGroupId === group.id }
                )}
                onClick={() => onClick(group)}
                onMouseEnter={() => highlightLocationGroupId(group.id)}
                onMouseLeave={() => highlightLocationGroupId(undefined)}
              >
                <div className={styles.title}>
                  {formatFriendlyRelativeTime(intl, location.createdAt, true)}
                </div>
                {location.crmActivity ? (
                  <ButtonLink onClick={(e) => handleActivityClick(e, location.crmActivity)}>
                    {getSubTitle(location.type, location.crmActivity)}
                  </ButtonLink>
                ) : (
                  <div className={styles.checkInType}>
                    {getSubTitle(location.type, location.crmActivity)}
                  </div>
                )}
                {location.type !== LocationType.CHECK_IN && (
                  <div className={styles.coordinates}>
                    ({location.geoPoint.coordinates[1]}, {location.geoPoint.coordinates[0]})
                  </div>
                )}
              </div>
              {index === last24hoursIndex && (
                <div className={styles.locationTitle}>
                  {intl.formatMessage({
                    id: "map.layer.userLocation.locations.last24Hours",
                    defaultMessage: "Last 24 hours",
                    description: "Map - user location pane - location list - last 24 hours",
                  })}
                </div>
              )}
              {index === earlierIndex && (
                <div className={styles.locationTitle}>
                  {intl.formatMessage({
                    id: "map.layer.userLocation.locations.earlier",
                    defaultMessage: "Earlier",
                    description: "Map - user location pane - location list - earlier",
                  })}
                </div>
              )}
            </Timeline.Item>
          ))
        )}
      </Timeline>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  highlightedLocationId: getHighlightedLocationGroup(state),
  lastLocation: getLastLocationWithoutPeriod(state),
});

const mapDispatchToProps = {
  highlightLocationGroupId,
  showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(LocationRecords);
